import api from '@/api';
import { proxy, subscribe } from 'valtio';
import { subscribeKey } from 'valtio/utils';
import { normalizeData } from '@/utils';
import storeRematch from '@/store';
import createCache from '@/restore/oil-production-loss/cache';

import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import * as versions from './versions';

const ABORT_ERROR_CODE = 20;

const defaultState = {
    stratums: null,
    isLoading: false,
};

export const store = proxy({
    ...defaultState,
});

export const handlers = {
    onCheck(key) {
        return ({ target: { checked } }) => {
            store.stratums[key].checked = checked;
        };
    },
    onCheckAll(event) {
        if (isEmpty(store.stratums)) return;
        Object.keys(store.stratums).map(key => handlers.onCheck(key)(event));
    },
};

const createActions = () => {
    let kyController = new AbortController();
    const cache = createCache();

    return {
        getParams() {
            const values = Object.values(store.stratums ?? []);
            const checkedKeys = values.reduce((acc, item) => {
                const { stratum_id, checked } = item;

                if (checked) acc.push(stratum_id);
                return acc;
            }, []);

            return {
                stratumIds: checkedKeys,
            };
        },
        resetDefaultState() {
            kyController.abort();
            kyController = new AbortController();

            Object.entries(defaultState).map(([key, value]) => {
                store[key] = value;
            });
        },
        async fetch(versionId) {
            if (isNil(versionId)) return;

            store.isLoading = true;

            const {
                tree: {
                    calculationObject: { id: fieldId, parentVentureId: ventureId },
                },
            } = storeRematch.getState();

            const payload = {
                body: {
                    versionId: Number(versionId),
                    fieldId,
                    ventureId,
                },
                signal: kyController.signal,
            };

            try {
                const stratums = await cache.get(payload, api.reports.getStratums);
                const stratumsWithCheck = stratums.map(item => ({ ...item, checked: true }));

                store.stratums = proxy(normalizeData(stratumsWithCheck, 'stratum_id').data);

                store.isLoading = false;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Stratum by version fetch error', error);
                if (error.code !== ABORT_ERROR_CODE) store.isLoading = false;
            }
        },
    };
};

export const actions = createActions();

export const list = proxy({
    data: [],
});

subscribe(store, () => {
    const stratums = store.stratums;
    list.data = stratums ? Object.values(stratums) : [];
});

export const validate = {
    check() {
        const resultObject = {
            modelName: 'stratums',
            isValid: true,
        };

        const values = Object.values(store.stratums ?? []);
        const isChecked = values.some(stratum => stratum.checked);

        if (isChecked) return resultObject;

        resultObject.isValid = false;
        return resultObject;
    },
};

export const isStratumLoading = proxy({
    isLoading: false,
});

const updateIsStratumLoading = () => {
    isStratumLoading.isLoading = store.isLoading || versions.store.isLoading;
};

subscribe(store, updateIsStratumLoading);
subscribe(versions.store, updateIsStratumLoading);

subscribeKey(versions.store, 'activeVersion', versionId => {
    if (!store.isLoading) actions.fetch(versionId);
});
